// ============================================================================
// Canonical Fleet detail route contract
// ============================================================================
//
// EquipmentHome deliberately owns a broad /fleet/* legacy route space: its
// index, fuel log, two entry-record namespaces, and historical unknown suffixes
// all arrive at the same container. The independent detail release is narrower:
// exactly one decoded-safe, unreserved record segment. This neutral predicate
// is shared by the resolver and the release boundary so a listed ledger row
// cannot turn a nested or alias path into a boot capability.
// ============================================================================

export const FLEET_DETAIL_PATHNAME_PREFIX = '/fleet/';

// These names do not identify standalone Fleet records in the canonical
// release space. `fuel-log` is the index's alternate view, `materials` is a
// retired exact alias, and the two entry namespaces must retain their own
// /fleet/<namespace>/<id> route contracts. Holding a bare ambiguous namespace
// on legacy is conservative and preserves the existing renderer behavior.
const RESERVED_FLEET_DETAIL_SEGMENTS = new Set(['fuel-log', 'materials', 'fueling', 'checklist']);

/**
 * Match exactly one meaningful Fleet record segment. Decode before the final
 * checks so encoded separators/control characters cannot broaden a record
 * release into a nested legacy route. Search and hash are intentionally not
 * part of this pathname contract; callers provide `location.pathname` only.
 */
export function isCanonicalFleetDetailPathname(pathname: unknown): pathname is string {
  if (typeof pathname !== 'string' || !pathname.startsWith(FLEET_DETAIL_PATHNAME_PREFIX)) return false;

  const rawSlug = pathname.slice(FLEET_DETAIL_PATHNAME_PREFIX.length);
  if (
    rawSlug === '' ||
    rawSlug.includes('/') ||
    rawSlug.includes('\\') ||
    rawSlug.includes('\0') ||
    rawSlug.includes('\r') ||
    rawSlug.includes('\n') ||
    rawSlug.includes('?') ||
    rawSlug.includes('#')
  ) {
    return false;
  }

  try {
    const slug = decodeURIComponent(rawSlug);
    return (
      slug.trim() !== '' &&
      slug !== '.' &&
      slug !== '..' &&
      !RESERVED_FLEET_DETAIL_SEGMENTS.has(slug) &&
      !slug.includes('/') &&
      !slug.includes('\\') &&
      !slug.includes('\0') &&
      !slug.includes('\r') &&
      !slug.includes('\n') &&
      !slug.includes('?') &&
      !slug.includes('#')
    );
  } catch {
    return false;
  }
}
